/**
 * Job, run, queue, and related types for the API layer.
 */

import type { AdapterRecord, JobAdapterRecord } from "./adapter.ts";
import type { SuburbList } from "./suburb-list.ts";

export type { AdapterRecord, JobAdapterRecord };

export type JobStatus =
  | "idle"
  | "running"
  | "completed"
  | "failed"
  | "cancelled"
  | "scheduled"
  | "paused";
export type JobRunStatus = "running" | "completed" | "failed" | "cancelled";
export type JobRunPhase =
  | "pending"
  | "discovery"
  | "scraping"
  | "exporting"
  | "completed"
  | "failed"
  | "paused"
  | "cancelled";
export type RunType = "immediate" | "scheduled";

export interface CrawlJobConfig {
  listingUrls?: string[];
  maxPages?: number;
  /** A GLOBAL_TIME_RANGE_OPTIONS value; "since-last-success" is resolved per run by job-orchestrator. */
  timeRange?: string;
  /** First-run window in days for timeRange "since-last-success". */
  rollingFirstRunDays?: number;
  dateFilterMode?: "relative" | "absolute";
  listingType?: "buy" | "rent" | "sold" | "any";
  propertyTypes?: string[];
  dateFrom?: string;
  dateTo?: string;
  minPrice?: number;
  maxPrice?: number;
  providerMode?: "auto" | "fallback" | "forced";
  provider?: "firecrawl_scrape" | "firecrawl_agent" | "zyte" | "direct";
  providerPreference?: string[];
  debugOptions?: {
    pauseOnSubmit?: boolean;
  };
  /** Which discovered detail URLs a run fetches; resolveCrawlPolicy in @repo/adapter-core. */
  crawlPolicy?: {
    skipComplete?: boolean;
    completeMissing?: boolean;
    forceRecrawl?: boolean;
  };
}

export interface Job {
  id: string;
  name: string;
  adapter_id: string | null;
  created_by: string | null;
  status: JobStatus;
  run_type: RunType;
  cron_expression: string | null;
  output_format: "json" | "csv";
  config: CrawlJobConfig;
  metadata?: Record<string, unknown>;
  last_run_at: string | null;
  next_run_at: string | null;
  created_at: string;
  updated_at: string;
  adapters?: AdapterRecord;
  job_adapters?: JobAdapterRecord[];
  job_suburbs?: JobSuburbRecord[];
  job_suburb_lists?: JobSuburbListRecord[];
}

export interface JobSuburbListRecord {
  job_id: string;
  suburb_list_id: string;
  created_at: string;
  created_by: string | null;
  suburb_lists?: SuburbList;
}

export interface JobSuburbRecord {
  job_id: string;
  suburb_id: string;
  created_at: string;
  suburbs?: SuburbRecord;
}

export interface SuburbRecord {
  id: string;
  suburb_name: string;
  locality: string;
  postcode: string;
  state: string;
  region: string | null;
  latitude: number | null;
  longitude: number | null;
  created_at: string;
}

export interface JobRun {
  id: string;
  job_id: string;
  status: JobRunStatus;
  phase: JobRunPhase | null;
  started_at: string;
  completed_at: string | null;
  discovery_completed_at: string | null;
  records_scraped: number;
  urls_discovered: number | null;
  urls_scraped: number | null;
  error_message: string | null;
  output_file_path: string | null;
  metadata: JobRunMetadata | null;
}

export interface JobRunMetadata {
  suburbs?: number;
  adapters?: number;
  total_urls?: number;
  urls_discovered?: number;
  urls_scraped?: number;
  urls_failed?: number;
  current_suburb?: string;
  current_adapter?: string;
  phase?: string;
  [key: string]: unknown;
}

export interface ScrapedProperty {
  id: string;
  job_run_id: string;
  url: string;
  scraped_at: string;
  price: number | null;
  price_display: string | null;
  address: string | null;
  suburb: string | null;
  postcode: string | null;
  state: string | null;
  bedrooms: number | null;
  bathrooms: number | null;
  parking: number | null;
  property_size: string | null;
  property_size_unit: string | null;
  property_type: string | null;
  description: string | null;
  raw_data: Record<string, unknown> | null;
  created_at: string;
}

export interface ScrapeQueueItem {
  id: string;
  job_id: string;
  job_run_id: string | null;
  adapter_id: string;
  suburb_id: string | null;
  url: string;
  status: "pending" | "in_progress" | "completed" | "failed";
  attempts: number;
  last_error: string | null;
  next_retry_at: string | null;
  locked_at: string | null;
  locked_by: string | null;
  scraped_data: Record<string, unknown> | null;
  scraped_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface JobWithRelations extends Job {
  job_suburbs?: (JobSuburbRecord & { suburbs: SuburbRecord })[];
  job_adapters?: (JobAdapterRecord & { adapters: AdapterRecord })[];
  job_suburb_lists?: (JobSuburbListRecord & { suburb_lists: SuburbList })[];
}

export function getJobAdapterNames(job: Job | JobWithRelations): string[] {
  if (job.job_adapters && job.job_adapters.length > 0) {
    // Sort alphabetically so the list is stable across refetches. Supabase
    // returns join rows in arbitrary order, which made the JobsTable's
    // first-shown adapters flip between refreshes (e.g. "Barry Plant,
    // Belle Property + 8" → "Kay & Burton, Buxton + 8") even though the
    // underlying data was identical.
    return job.job_adapters
      .map((ja) => ja.adapters?.name)
      .filter((name): name is string => !!name)
      .sort((a, b) => a.localeCompare(b));
  }
  if (job.adapters?.name) {
    return [job.adapters.name];
  }
  return [];
}

export interface JobGeographySummary {
  manualSuburbCount: number;
  suburbListCount: number;
}

/** Counts manual suburbs and linked suburb lists separately (lists are not suburbs). */
export function getJobGeographySummary(job: Job | JobWithRelations): JobGeographySummary {
  return {
    manualSuburbCount: job.job_suburbs?.length ?? 0,
    suburbListCount: job.job_suburb_lists?.length ?? 0,
  };
}

/** Short label for jobs table and tooling: e.g. "3 suburbs · 2 lists", "2 lists". */
export function formatJobGeographySummary(summary: JobGeographySummary): string {
  const { manualSuburbCount, suburbListCount } = summary;
  const parts: string[] = [];
  if (manualSuburbCount > 0) {
    parts.push(`${manualSuburbCount} suburb${manualSuburbCount !== 1 ? "s" : ""}`);
  }
  if (suburbListCount > 0) {
    parts.push(`${suburbListCount} list${suburbListCount !== 1 ? "s" : ""}`);
  }
  if (parts.length === 0) return "No geography configured";
  return parts.join(" · ");
}

/** One-liner for UI: formatted geography from a job row. */
export function formatJobGeography(job: Job | JobWithRelations): string {
  return formatJobGeographySummary(getJobGeographySummary(job));
}

export function isMultiAdapterJob(job: Job | JobWithRelations): boolean {
  return (job.job_adapters && job.job_adapters.length > 0) || !job.adapter_id;
}
